import * as React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Skeleton,
  Drawer,
  Divider,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import axios from "axios";
import { useAuth } from "../services/auth.service";


const formatMoney = (value, currency = "USD") => {
  const num = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusChip = (status) => {
  const s = String(status || "").toUpperCase();
  const common = {
    size: "small",
    variant: "filled",
    sx: {
      height: 22,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      borderRadius: 999,
    },
  };
  if (s === "COMPLETED")
    return (
      <Chip
        {...common}
        label="Completado"
        sx={{ ...common.sx, bgcolor: "#fcdeb5", color: "#574425" }}
      />
    );
  if (s === "PENDING")
    return (
      <Chip
        {...common}
        label="Pendiente"
        sx={{ ...common.sx, bgcolor: "#d0e1fb", color: "#54647a" }}
      />
    );
  if (s === "FAILED")
    return (
      <Chip
        {...common}
        label="Fallido"
        sx={{ ...common.sx, bgcolor: "#ffdad6", color: "#93000a" }}
      />
    );
  return <Chip {...common} label={s === "COMPLETED" ? "Completado" : s === "PENDING" ? "Pendiente" : s === "FAILED" ? "Fallido" : (s || "—")} sx={{ ...common.sx, bgcolor: "#eceef0", color: "#45464d" }} />;
};

const maskAlias = (alias) => {
  if (!alias) return "—";
  const clean = alias.trim();
  if (clean.startsWith("+") || /^\d+$/.test(clean)) {
    if (clean.length > 7) {
      return `${clean.slice(0, 4)} **** ${clean.slice(-4)}`;
    }
  }
  return clean;
};

export default function TransaccionesYappy() {
  const { user } = useAuth();
  
  const [transactions, setTransactions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const [query, setQuery] = React.useState("");
  const [searchVal, setSearchVal] = React.useState("");
  const [status, setStatus] = React.useState("ALL");
  const [type, setType] = React.useState("ALL");
  const [role, setRole] = React.useState("ALL");
  const [category, setCategory] = React.useState("ALL");
  const [debitorAlias, setDebitorAlias] = React.useState("");
  const [debitorName, setDebitorName] = React.useState("");
  const [creditorAlias, setCreditorAlias] = React.useState("");
  const [creditorName, setCreditorName] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [dateField, setDateField] = React.useState("payment_date");
  const [sortBy, setSortBy] = React.useState("payment_date");
  const [sortOrder, setSortOrder] = React.useState("desc");

  // Single transaction details & drawer state
  const [selectedTxId, setSelectedTxId] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState(null);
  const [drawerLoading, setDrawerLoading] = React.useState(false);
  const [drawerError, setDrawerError] = React.useState(null);

  // Manual search/lookup input
  const [lookupId, setLookupId] = React.useState("");

  const fetchTransactions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (user?.accessToken) {
        headers["x-access-token"] = user.accessToken;
        headers["Authorization"] = `Bearer ${user.accessToken}`;
      }
      
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      
      if (query.trim()) params.append("search", query.trim());
      if (status !== "ALL") params.append("status", status);
      if (type !== "ALL") params.append("type", type);
      if (role !== "ALL") params.append("role", role);
      if (category !== "ALL") params.append("category", category);
      if (debitorAlias.trim()) params.append("debitorAlias", debitorAlias.trim());
      if (debitorName.trim()) params.append("debitorName", debitorName.trim());
      if (creditorAlias.trim()) params.append("creditorAlias", creditorAlias.trim());
      if (creditorName.trim()) params.append("creditorName", creditorName.trim());
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      params.append("dateField", dateField);
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API}/yappy/transactions?${params.toString()}`,
        { headers }
      );
      
      if (response.data && response.data.success) {
        setTransactions(response.data.transactions || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        setError("Error al cargar las transacciones");
      }
    } catch (err) {
      console.error("Error fetching Yappy transactions:", err);
      setError(
        err.response?.data?.message ||
        "Error de red al obtener las transacciones de Yappy"
      );
    } finally {
      setLoading(false);
    }
  }, [
    user?.accessToken,
    page,
    limit,
    query,
    status,
    type,
    role,
    category,
    debitorAlias,
    debitorName,
    creditorAlias,
    creditorName,
    startDate,
    endDate,
    dateField,
    sortBy,
    sortOrder
  ]);

  React.useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  React.useEffect(() => {
    if (!selectedTxId) return;

    const fetchSingleTx = async () => {
      setDrawerLoading(true);
      setDrawerError(null);
      setDrawerData(null);
      try {
        const headers = {
          "Content-Type": "application/json",
        };
        if (user?.accessToken) {
          headers["x-access-token"] = user.accessToken;
          headers["Authorization"] = `Bearer ${user.accessToken}`;
        }
        
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API}/yappy/movement/${selectedTxId}`,
          { headers }
        );

        if (response.data && response.data.success) {
          setDrawerData(response.data.transaction);
        } else {
          setDrawerError("No se pudo obtener el detalle de la transacción");
        }
      } catch (err) {
        console.error("Error fetching single transaction:", err);
        setDrawerError(
          err.response?.data?.message ||
          "Error de red al obtener la transacción"
        );
      } finally {
        setDrawerLoading(false);
      }
    };

    fetchSingleTx();
  }, [selectedTxId, user?.accessToken]);

  const dateRangeString = React.useMemo(() => {
    if (!transactions || transactions.length === 0) return "Sin transacciones";
    const dates = transactions
      .map(t => t.payment_date || t.registration_date)
      .filter(Boolean)
      .map(d => new Date(d))
      .filter(d => !isNaN(d.getTime()));
    if (dates.length === 0) return "Sin rango de fecha";
    
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    const format = (d) => d.toLocaleDateString("es-PA", { year: "numeric", month: "2-digit", day: "2-digit" });
    return `${format(minDate)} - ${format(maxDate)}`;
  }, [transactions]);

  const filtered = transactions;

  const totals = React.useMemo(() => {
    const volume = filtered.reduce((acc, t) => acc + Number(t.charge?.amount ?? 0), 0);
    const fees = filtered.reduce((acc, t) => acc + Number(t.fee?.amount ?? 0), 0);

    return { volume, fees, count: filtered.length };
  }, [filtered]);

  const Card = ({ label, icon, value, sub }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: "#ffffff",
        border: "1px solid rgba(198,198,205,0.15)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 112,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ color: "#45464d", fontSize: 11, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Box sx={{ color: "#79849a", fontSize: 18, lineHeight: 1 }}>{icon}</Box>
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.6, color: "#191c1e" }}>
          {value}
        </Typography>
        {sub ? (
          <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 800, color: "#574425" }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: "#f7f9fb", minHeight: "calc(100vh - 64px)" }}>
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: "auto" }}>
        <Stack spacing={3.5}>
          {/* Page header */}
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 900, letterSpacing: -0.8, color: "#191c1e" }}>
                Transacciones Yappy
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, fontWeight: 600, color: "#45464d" }}>
                Monitorizando y visualizando transacciones registradas de Yappy vía webhook en tiempo real.
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              onClick={fetchTransactions}
              sx={{
                bgcolor: "#000000",
                color: "#ffffff",
                px: 2.5,
                py: 1.2,
                borderRadius: 3,
                fontWeight: 900,
                textTransform: "none",
                "&:hover": { bgcolor: "#111c2d" },
              }}
              startIcon={<span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>}
            >
              Actualizar
            </Button>
          </Box>

          {/* Summary bento */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card
                label="Volumen de la Página"
                icon={<span className="material-symbols-outlined">payments</span>}
                value={formatMoney(totals.volume, "USD")}
                sub="Suma total de la página actual"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                label="Comisiones de la Página"
                icon={<span className="material-symbols-outlined">account_balance_wallet</span>}
                value={formatMoney(totals.fees, "USD")}
                sub={`Promedio: ${formatMoney(totals.count ? (totals.fees / totals.count) : 0, "USD")} por txn`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                label="Transacciones Activas"
                icon={<span className="material-symbols-outlined">sync</span>}
                value={String(totals.count)}
                sub={`Mostrando ${totals.count} de ${total} transacciones en total`}
              />
            </Grid>
          </Grid>

          {/* Filtros avanzados a ancho completo */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#ffffff",
              border: "1px solid rgba(198,198,205,0.15)",
              boxShadow: "0 10px 30px rgba(25, 28, 30, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1.5, mb: 2.5, borderBottom: "1px solid rgba(198,198,205,0.10)" }}>
              <span className="material-symbols-outlined" style={{ color: "#191c1e" }}>
                tune
              </span>
              <Typography sx={{ fontSize: 16, fontWeight: 900, letterSpacing: -0.2 }}>
                Filtros del Libro de Transacciones
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Horizonte de Tiempo
                </Typography>
                <TextField
                  value={dateRangeString}
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                          calendar_today
                        </span>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Elementos por Página
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value={10}>10 transacciones</MenuItem>
                    <MenuItem value={25}>25 transacciones</MenuItem>
                    <MenuItem value={50}>50 transacciones</MenuItem>
                    <MenuItem value={100}>100 transacciones</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Estado de Flujo
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Estados</MenuItem>
                    <MenuItem value="COMPLETED">Completado</MenuItem>
                    <MenuItem value="PENDING">Pendiente</MenuItem>
                    <MenuItem value="FAILED">Fallido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Tipo de Transacción
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value);
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Tipos</MenuItem>
                    <MenuItem value="TXN-COM">Comercio (TXN-COM)</MenuItem>
                    <MenuItem value="TXN-ECOM">E-Commerce (TXN-ECOM)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Rol de Transacción
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todos los Roles</MenuItem>
                    <MenuItem value="CREDIT">Crédito</MenuItem>
                    <MenuItem value="DEBIT">Débito</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d" }}>
                  Categoría
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setPage(1);
                    }}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="ALL">Todas las Categorías</MenuItem>
                    <MenuItem value="INTRABANK">Intrabancaria (INTRABANK)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Ordenación
                </Typography>
                <Stack spacing={1}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{
                        bgcolor: "#f7f9fb",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#000000" },
                      }}
                    >
                      <MenuItem value="payment_date">Fecha de Pago</MenuItem>
                      <MenuItem value="createdAt">Creado en DB</MenuItem>
                      <MenuItem value="registration_date">Fecha de Registro</MenuItem>
                      <MenuItem value="charge.amount">Monto del Pago</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Sentido de Orden
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    sx={{
                      bgcolor: "#f7f9fb",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#000000" },
                    }}
                  >
                    <MenuItem value="desc">Descendente</MenuItem>
                    <MenuItem value="asc">Ascendente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Filtro de Emisor (Debitor)
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Nombre"
                    value={debitorName}
                    onChange={(e) => setDebitorName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                  <TextField
                    label="Alias / Teléfono"
                    value={debitorAlias}
                    onChange={(e) => setDebitorAlias(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Filtro de Receptor (Creditor)
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Nombre"
                    value={creditorName}
                    onChange={(e) => setCreditorName(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                  <TextField
                    label="Alias / Teléfono"
                    value={creditorAlias}
                    onChange={(e) => setCreditorAlias(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 10, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase", color: "#45464d", mb: 1 }}>
                  Filtrar por Rango de Fechas
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
                    <Select
                      value={dateField}
                      onChange={(e) => setDateField(e.target.value)}
                      sx={{
                        bgcolor: "#f7f9fb",
                        borderRadius: 2,
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#000000" },
                      }}
                    >
                      <MenuItem value="payment_date">Pago</MenuItem>
                      <MenuItem value="createdAt">DB</MenuItem>
                      <MenuItem value="registration_date">Registro</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                  <Typography sx={{ fontSize: 12, color: "#45464d" }}>a</Typography>
                  <TextField
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f7f9fb", borderRadius: 2 } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
                <Button
                  variant="text"
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    fontWeight: 900,
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                    fontSize: 10,
                    color: "#45464d",
                    "&:hover": { bgcolor: "#eceef0" },
                  }}
                  onClick={() => {
                    setQuery("");
                    setSearchVal("");
                    setStatus("ALL");
                    setType("ALL");
                    setRole("ALL");
                    setCategory("ALL");
                    setDebitorAlias("");
                    setDebitorName("");
                    setCreditorAlias("");
                    setCreditorName("");
                    setStartDate("");
                    setEndDate("");
                    setDateField("payment_date");
                    setSortBy("payment_date");
                    setSortOrder("desc");
                    setPage(1);
                  }}
                >
                  Restablecer Filtros
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={fetchTransactions}
                  sx={{
                    bgcolor: "#000000",
                    color: "#ffffff",
                    borderRadius: 3,
                    px: 4,
                    py: 1,
                    fontWeight: 900,
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                    fontSize: 11,
                    "&:hover": { bgcolor: "#111c2d" },
                  }}
                >
                  Actualizar Datos
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Libro de transacciones a ancho completo */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "#ffffff",
              boxShadow: "0 20px 40px rgba(25, 28, 30, 0.06)",
            }}
          >
              {/* Table toolbar */}
              <Box
                sx={{
                  p: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  borderBottom: "1px solid rgba(198,198,205,0.15)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.3, color: "#191c1e" }}>
                    Libro de Transacciones Detallado
                  </Typography>
                  <TextField
                    value={searchVal}
                    onChange={(e) => setSearchVal(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        setQuery(searchVal);
                        setPage(1);
                      }
                    }}
                    placeholder="Buscar en el libro..."
                    size="small"
                    sx={{
                      width: { xs: "100%", sm: 240 },
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#f2f4f6",
                        borderRadius: 999,
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#000000" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                            search
                          </span>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setQuery(searchVal);
                              setPage(1);
                            }}
                            edge="end"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#000000" }}>
                              arrow_forward
                            </span>
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        const cleanId = (lookupId || "").trim();
                        if (cleanId) {
                          setSelectedTxId(cleanId);
                          setDrawerOpen(true);
                        }
                      }
                    }}
                    placeholder="Buscar ID de Transacción..."
                    size="small"
                    sx={{
                      width: { xs: "100%", sm: 260 },
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#f2f4f6",
                        borderRadius: 999,
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "rgba(198,198,205,0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#000000" },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#45464d" }}>
                            find_in_page
                          </span>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const cleanId = (lookupId || "").trim();
                              if (cleanId) {
                                setSelectedTxId(cleanId);
                                setDrawerOpen(true);
                              }
                            }}
                            edge="end"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#000000" }}>
                              arrow_forward
                            </span>
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Chip
                    label={`Total: ${total}`}
                    size="small"
                    sx={{
                      height: 26,
                      bgcolor: "#e6f4ea",
                      color: "#137333",
                      fontWeight: 900,
                      borderRadius: 999,
                      letterSpacing: 0.4,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    sx={{
                      borderRadius: 2,
                      color: "#45464d",
                      "&:hover": { bgcolor: "#f2f4f6" },
                    }}
                    aria-label="download"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                      download
                    </span>
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      borderRadius: 2,
                      color: "#45464d",
                      "&:hover": { bgcolor: "#f2f4f6" },
                    }}
                    aria-label="print"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                      print
                    </span>
                  </IconButton>
                </Box>
              </Box>
 
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        "ID de Transacción",
                        "Fecha de Ejecución",
                        "Emisor y Origen",
                        "Receptor",
                        "Tipo",
                        "Categoría",
                        "Monto",
                        "Comisión",
                        "Estado",
                      ].map((h, idx) => (
                        <TableCell
                          key={h}
                          sx={{
                            bgcolor: "#f2f4f6",
                            color: "#45464d",
                            fontSize: 11,
                            fontWeight: 900,
                            letterSpacing: 1.6,
                            textTransform: "uppercase",
                            borderBottom: "1px solid rgba(198,198,205,0.15)",
                            ...(idx >= 6 ? { textAlign: "right" } : null),
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: limit }).map((_, rIdx) => (
                        <TableRow key={`skeleton-row-${rIdx}`}>
                          {Array.from({ length: 9 }).map((_, cIdx) => (
                            <TableCell key={`skeleton-cell-${rIdx}-${cIdx}`}>
                              <Skeleton variant="text" animation="wave" height={24} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 10, textAlign: "center", color: "#93000a" }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 36, verticalAlign: "middle" }}>error</span>
                          <Typography sx={{ mt: 1, fontWeight: 900, fontSize: 14 }}>Error al cargar datos</Typography>
                          <Typography sx={{ fontSize: 12, mt: 0.5, color: "#45464d" }}>{error}</Typography>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={fetchTransactions}
                            sx={{ mt: 2, color: "#000000", borderColor: "#000000", "&:hover": { borderColor: "#000000", bgcolor: "rgba(0,0,0,0.04)" } }}
                          >
                            Reintentar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 6, textAlign: "center", color: "#45464d" }}>
                          <Typography sx={{ fontWeight: 900 }}>Sin resultados</Typography>
                          <Typography sx={{ fontSize: 12, mt: 0.5 }}>Ajusta los filtros o tu búsqueda.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((t) => (
                        <TableRow
                          key={t.id}
                          hover
                          onClick={() => {
                            setSelectedTxId(t.id);
                            setDrawerOpen(true);
                          }}
                          sx={{
                            "&:hover": { bgcolor: "#e6e8ea" },
                            cursor: "pointer",
                          }}
                        >
                          <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, fontWeight: 700, color: "#000000" }}>
                            {t.id}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 600 }}>
                            {formatDateTime(t.payment_date)}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#191c1e", lineHeight: 1.15 }}>
                              {t.debitor?.complete_name || "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#45464d", opacity: 0.85, mt: 0.4 }}>
                              {maskAlias(t.debitor?.alias)} · {t.debitor?.bank_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#191c1e", lineHeight: 1.15 }}>
                              {t.creditor?.complete_name || "—"}
                            </Typography>
                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#45464d", opacity: 0.85, mt: 0.4 }}>
                              {maskAlias(t.creditor?.alias)} · {t.creditor?.bank_name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>{t.type}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>{t.category}</TableCell>
                          <TableCell sx={{ fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 900 }}>
                            {formatMoney(t.charge?.amount, t.charge?.currency)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#45464d", fontWeight: 700 }}>
                            {formatMoney(t.fee?.amount, t.fee?.currency)}
                          </TableCell>
                          <TableCell>{statusChip(t.status)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
 
              {/* Footer */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: "#f2f4f6",
                  borderTop: "1px solid rgba(198,198,205,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700 }}>
                  Mostrando <span style={{ color: "#191c1e", fontWeight: 900 }}>{total === 0 ? 0 : ((page - 1) * limit) + 1} - {Math.min(page * limit, total)}</span> de{" "}
                  <span style={{ color: "#191c1e", fontWeight: 900 }}>{total}</span> transacciones
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Button
                    size="small"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{ minWidth: 36, px: 1, borderRadius: 2, fontWeight: 900, color: "#191c1e" }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
                  </Button>
                  
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = 1;
                    let end = totalPages;
                    if (totalPages > maxVisible) {
                      start = Math.max(1, page - 2);
                      end = Math.min(totalPages, page + 2);
                      if (start === 1) {
                        end = maxVisible;
                      } else if (end === totalPages) {
                        start = totalPages - maxVisible + 1;
                      }
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    return pages.map((p) => (
                      <Button
                        key={p}
                        size="small"
                        variant={page === p ? "contained" : "text"}
                        disableElevation={page === p}
                        onClick={() => setPage(p)}
                        sx={{
                          minWidth: 36,
                          px: 1.5,
                          borderRadius: 2,
                          bgcolor: page === p ? "#000000" : "transparent",
                          color: page === p ? "#ffffff" : "#191c1e",
                          fontWeight: 900,
                          "&:hover": { bgcolor: page === p ? "#111c2d" : "#eceef0" },
                        }}
                      >
                        {p}
                      </Button>
                    ));
                  })()}

                  <Button
                    size="small"
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    sx={{ minWidth: 36, px: 1, borderRadius: 2, fontWeight: 900, color: "#191c1e" }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Tarjeta de Cifrado de Bóveda */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#111c2d",
                color: "#ffffff",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <span className="material-symbols-outlined" style={{ color: "#fcdeb5", fontVariationSettings: "'FILL' 1", fontSize: 26 }}>
                verified_user
              </span>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Cifrado de Bóveda Activo</Typography>
                <Typography sx={{ mt: 0.5, fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                  Los registros de auditoría de extremo a extremo están bloqueados criptográficamente y verificados por el libro mayor central. (demo)
                </Typography>
              </Box>
            </Paper>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              setSelectedTxId(null);
            }}
            PaperProps={{
              sx: {
                width: { xs: "100%", sm: 500 },
                borderLeft: "1px solid rgba(198,198,205,0.15)",
                boxShadow: "-10px 0 40px rgba(0,0,0,0.05)"
              }
            }}
          >
            <Box sx={{ p: 3, height: "100%", bgcolor: "#f7f9fb", overflowY: "auto" }}>
              {/* Drawer Header */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#191c1e" }}>
                  Detalle de Transacción
                </Typography>
                <IconButton
                  onClick={() => {
                    setDrawerOpen(false);
                    setSelectedTxId(null);
                  }}
                  sx={{ bgcolor: "#eceef0", "&:hover": { bgcolor: "#e6e8ea" } }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                </IconButton>
              </Box>

              {drawerLoading ? (
                <Stack spacing={3}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="60%" height={28} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                    <Skeleton variant="rectangular" height={50} sx={{ mt: 2, borderRadius: 2 }} />
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1.5 }} />
                    <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "#ffffff" }}>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1.5 }} />
                    <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
                  </Paper>
                </Stack>
              ) : drawerError ? (
                <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 48, color: "#93000a" }}>error</span>
                  <Typography sx={{ mt: 2, fontWeight: 900, color: "#191c1e" }}>
                    No se pudo cargar el detalle
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: 13, color: "#45464d" }}>
                    {drawerError}
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      const currentId = selectedTxId;
                      setSelectedTxId(null);
                      setTimeout(() => setSelectedTxId(currentId), 50);
                    }}
                    sx={{ mt: 3, bgcolor: "#000000", color: "#ffffff", borderRadius: 3, px: 3, py: 1 }}
                  >
                    Reintentar
                  </Button>
                </Box>
              ) : drawerData ? (
                <Stack spacing={3}>
                  {/* Status & Amount Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, fontWeight: 900, color: "#79849a" }}>
                        {drawerData.id}
                      </Typography>
                      {statusChip(drawerData.status)}
                    </Box>
                    <Typography sx={{ fontSize: 32, fontWeight: 900, color: "#191c1e", letterSpacing: -0.8 }}>
                      {formatMoney(drawerData.charge?.amount, drawerData.charge?.currency)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 800, mt: 0.5 }}>
                      Comisión: {formatMoney(drawerData.fee?.amount, drawerData.fee?.currency)}
                    </Typography>
                    
                    {drawerData.description && (
                      <Box sx={{ mt: 2.5, p: 1.5, bgcolor: "#f7f9fb", borderRadius: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 0.8 }}>
                          Descripción
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#191c1e", fontWeight: 600 }}>
                          {drawerData.description}
                        </Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Debitor & Creditor Info */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 1.2, mb: 2 }}>
                      Información de las Partes
                    </Typography>
                    
                    {/* Debitor */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Emisor / Debitor
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#191c1e", mt: 0.2 }}>
                        {drawerData.debitor?.complete_name || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700, mt: 0.2 }}>
                        Alias: {maskAlias(drawerData.debitor?.alias)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 600, mt: 0.2 }}>
                        {drawerData.debitor?.bank_name}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: "rgba(198,198,205,0.15)" }} />

                    {/* Creditor */}
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "#79849a", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Receptor / Creditor
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#191c1e", mt: 0.2 }}>
                        {drawerData.creditor?.complete_name || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#45464d", fontWeight: 700, mt: 0.2 }}>
                        Alias: {maskAlias(drawerData.creditor?.alias)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#79849a", fontWeight: 600, mt: 0.2 }}>
                        {drawerData.creditor?.bank_name}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Transaction Metadata */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid rgba(198,198,205,0.15)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#45464d", textTransform: "uppercase", letterSpacing: 1.2, mb: 2 }}>
                      Metadata de Transacción
                    </Typography>
                    
                    <Stack spacing={1.8}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Número de Transacción</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>
                          {drawerData.number ? `${drawerData.number.slice(0, 8)}...` : "—"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Tipo</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.type === "TXN-COM" ? "Comercio (TXN-COM)" : drawerData.type === "TXN-ECOM" ? "E-Commerce (TXN-ECOM)" : (drawerData.type || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Rol</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.role === "CREDIT" ? "Crédito" : drawerData.role === "DEBIT" ? "Débito" : (drawerData.role || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Categoría</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 800 }}>
                          {drawerData.category === "INTRABANK" ? "Intrabancaria" : (drawerData.category || "—")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Pago</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                          {formatDateTime(drawerData.payment_date)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Registro</Typography>
                        <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                          {formatDateTime(drawerData.registration_date)}
                        </Typography>
                      </Box>

                      {drawerData.cut_off_date && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: 12, color: "#79849a", fontWeight: 700 }}>Fecha de Corte</Typography>
                          <Typography sx={{ fontSize: 12, color: "#191c1e", fontWeight: 700 }}>
                            {drawerData.cut_off_date}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              ) : null}
            </Box>
          </Drawer>
        </Stack>
      </Box>
    </Box>
  );
}

